// Column-state catalog + visibility/width persistence helpers for the
// AuditTable. Lives in a .ts module (separate from AuditTable.tsx) so
// react-refresh can reload the component without warning about non-
// component exports.

export type AuditColumnKey =
  | "time"
  | "actor"
  | "role"
  | "location"
  | "action"
  | "details"
  | "request_id"
  | "source_ip"
  | "menu";

export interface AuditColumnDef {
  key: AuditColumnKey;
  label: string;
  defaultWidth: number; // px
  /** Whether the user can hide this column. `false` for time/actor/action/menu. */
  hideable: boolean;
  /** Whether this column is shown by default (off-by-default for Request id / IP). */
  defaultVisible: boolean;
}

export const AUDIT_COLUMNS: readonly AuditColumnDef[] = [
  { key: "time",       label: "Time",        defaultWidth: 140, hideable: false, defaultVisible: true  },
  { key: "actor",      label: "Actor",       defaultWidth: 280, hideable: false, defaultVisible: true  },
  { key: "role",       label: "Role",        defaultWidth: 130, hideable: true,  defaultVisible: true  },
  { key: "location",   label: "Location",    defaultWidth: 160, hideable: true,  defaultVisible: true  },
  { key: "action",     label: "Action",      defaultWidth: 320, hideable: false, defaultVisible: true  },
  { key: "details",    label: "Details",     defaultWidth: 320, hideable: true,  defaultVisible: true  },
  { key: "request_id", label: "Request ID",  defaultWidth: 200, hideable: true,  defaultVisible: false },
  { key: "source_ip",  label: "IP Address",  defaultWidth: 140, hideable: true,  defaultVisible: false },
  { key: "menu",       label: "",            defaultWidth: 48,  hideable: false, defaultVisible: true  },
] as const;

export const LS_WIDTHS = "rb-audit:col-widths";
export const LS_VISIBILITY = "rb-audit:col-visible";

function loadJsonLS<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function saveJsonLS(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // localStorage unavailable / full — silent
  }
}

export function defaultVisibility(): Record<AuditColumnKey, boolean> {
  const out = {} as Record<AuditColumnKey, boolean>;
  for (const col of AUDIT_COLUMNS) out[col.key] = col.defaultVisible;
  return out;
}

export function loadVisibility(): Record<AuditColumnKey, boolean> {
  return { ...defaultVisibility(), ...loadJsonLS<Record<AuditColumnKey, boolean>>(LS_VISIBILITY, {} as Record<AuditColumnKey, boolean>) };
}

export function saveVisibility(v: Record<AuditColumnKey, boolean>): void {
  saveJsonLS(LS_VISIBILITY, v);
}

export function loadWidths(): Partial<Record<AuditColumnKey, number>> {
  return loadJsonLS<Partial<Record<AuditColumnKey, number>>>(LS_WIDTHS, {});
}

export function saveWidths(w: Partial<Record<AuditColumnKey, number>>): void {
  saveJsonLS(LS_WIDTHS, w);
}
